// Central design tokens. Used via styled-components `ThemeProvider` (see src/index.js)
// and also mirrored as CSS variables in src/index.css for anything outside styled-components.

export const theme = {
  color: {
    // Brand
    salmon: "#F5A88A",        // refined from #FFC1A3 — deeper, better contrast on white
    salmonSoft: "#FBD6C4",    // tints for backgrounds / hover states
    salmonDeep: "#E38A66",    // press / active states
    slate: "#2E3A52",         // refined from #35435D — cooler and richer
    slateSoft: "#4A5874",
    slateDeep: "#1F2937",

    // Accent — used sparingly for secondary CTAs, links, step markers
    teal: "#2A6F6A",
    tealSoft: "#D4E6E3",

    // Surfaces
    bg: "#F8F4EF",            // warm off-white page background
    surface: "#FFFFFF",       // card surfaces
    surfaceSunk: "#F1EBE3",   // muted inset regions (form fields, etc)

    // Text
    text: "#1F2937",          // body
    textMuted: "#5B6476",
    textSubtle: "#8A93A3",

    // Borders
    border: "#E6DFD5",        // warm neutral divider
    borderStrong: "#C9C0B2",

    // Semantic
    success: "#28a745",
    error: "#DC3545",
    warning: "#F5B841",
    priceAccent: "#D63031",

    // Pricing / step backgrounds (legacy harmonized)
    stepNeutral: "#E4DED3",
    stepGreen: "#D4E0DD",
    stepBlue: "#C8D0E0",
    stepGray: "#CDC6BB",
  },

  font: {
    // Single-family system: Inter for everything. Hierarchy comes from
    // weight, size, and tracking — not font-family contrast.
    display: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif`,
    body: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif`,
  },

  // Modular type scale (1.250 — major third)
  type: {
    xs: "0.75rem",    // 12
    sm: "0.875rem",   // 14
    base: "1rem",     // 16
    md: "1.125rem",   // 18
    lg: "1.375rem",   // 22
    xl: "1.75rem",    // 28
    "2xl": "2.25rem", // 36
    "3xl": "3rem",    // 48
    "4xl": "3.75rem", // 60
  },

  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // 4px spacing grid
  space: {
    1: "0.25rem",  // 4
    2: "0.5rem",   // 8
    3: "0.75rem",  // 12
    4: "1rem",     // 16
    5: "1.5rem",   // 24
    6: "2rem",     // 32
    7: "2.5rem",   // 40
    8: "3rem",     // 48
    9: "4rem",     // 64
    10: "6rem",    // 96
  },

  radius: {
    sm: "6px",
    md: "10px",
    lg: "14px",
    xl: "20px",
    pill: "999px",
  },

  // Slate-tinted shadows — warmer than pure black
  shadow: {
    sm: "0 1px 2px rgba(46, 58, 82, 0.06), 0 1px 1px rgba(46, 58, 82, 0.04)",
    md: "0 4px 12px rgba(46, 58, 82, 0.08), 0 2px 4px rgba(46, 58, 82, 0.05)",
    lg: "0 16px 40px rgba(46, 58, 82, 0.12), 0 4px 8px rgba(46, 58, 82, 0.06)",
    ring: "0 0 0 3px rgba(245, 168, 138, 0.28)",  // focus ring using salmon
  },

  motion: {
    quick: "0.15s cubic-bezier(0.2, 0.8, 0.2, 1)",
    base: "0.25s cubic-bezier(0.2, 0.8, 0.2, 1)",
    slow: "0.5s cubic-bezier(0.2, 0.8, 0.2, 1)",
  },
};

export default theme;
